import { Controller } from "@hotwired/stimulus"
import { Popover } from "flowbite"

export default class extends Controller {
  static targets = ["popover", "popoverTrigger"]

  static values = {
    showOn: { type: String, default: "click" },
    focusWithin: { type: Boolean, default: false },
    placement: { type: String, default: "bottom" }
  }

  connect() {
    this.popover = new Popover(
      this.popoverTarget,
      this.popoverTriggerTarget,
      {
        placement: this.placementValue,
        // Flowbite's "click" triggerType wires both click AND focus as show events,
        // which would auto-open the popover on Tab focus and break the disclosure
        // pattern. In disclosure mode we manage trigger activation ourselves.
        triggerType: this.focusWithinValue ? "none" : this.showOnValue,
        onShow: () => {
          this.popoverTriggerTarget.setAttribute("aria-expanded", "true")
          this.popoverTarget.style.visibility = ""
          this.popoverTarget.style.pointerEvents = ""
        },
        onHide: () => {
          this.popoverTriggerTarget.setAttribute("aria-expanded", "false")
          this.popoverTarget.style.visibility = "hidden"
          this.popoverTarget.style.pointerEvents = "none"
        },
      }
    )
    this.popoverTarget.classList.remove("hidden")

    if (!this.focusWithinValue) return

    // Disclosure pattern: explicit activation (click, Enter, Space) opens the
    // popover. Keep it open while focus is anywhere in the trigger+body region.
    // Close on focus leaving the wrapper, on Escape, or on outside click.
    // AbortController unwires all listeners on disconnect. Abort any prior
    // controller first so a Stimulus reconnect (Turbo frame edges, hot reload)
    // does not orphan the previous listener set.
    this.abort?.abort()
    this.abort = new AbortController()
    const { signal } = this.abort
    this.popoverTriggerTarget.addEventListener("click", this.onTriggerActivate, { signal })
    this.popoverTriggerTarget.addEventListener("keydown", this.onTriggerKeydown, { signal })
    this.element.addEventListener("focusout", this.onFocusOut, { signal })
    this.element.addEventListener("keydown", this.onKeyDown, { signal })
    document.addEventListener("click", this.onOutsideClick, { signal })
  }

  onTriggerActivate = () => {
    this.popover.isVisible() ? this.popover.hide() : this.popover.show()
  }

  onTriggerKeydown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this.popover.isVisible() ? this.popover.hide() : this.popover.show()
    }
  }

  disconnect() {
    this.abort?.abort()
    this.popover?.destroy()
  }

  onFocusOut = (event) => {
    // Safari sets relatedTarget to null on some focus moves; treat as "still inside".
    if (event.relatedTarget && !this.element.contains(event.relatedTarget)) {
      this.popover.hide()
    }
  }

  onKeyDown = (event) => {
    if (event.key === "Escape") {
      this.popover.hide()
      this.popoverTriggerTarget.focus()
    }
  }

  onOutsideClick = (event) => {
    if (this.popover.isVisible() && !this.element.contains(event.target)) this.popover.hide()
  }
}
